import React, {useEffect, useRef, useState} from 'react';
import {makeStyles, Theme, createStyles} from '@material-ui/core/styles';
import UploadOrEditInsurance from '../UploadInsurance/UploadInsurance';
import {IShingrixConsentFormState} from '../ConsentForm/ShingrixConsentForm/ShingrixConsentForm';
import ConsentForm from '../ConsentForm/ConsentForm';
import {
  APP_PRIMARY_COLOR,
  APP_SECONDARY_COLOR,
  DEFAULT_WHITE_COLOR
} from 'src/style/variable';
import ScheduleAppointmentSlot, {
  IAppointmentType,
  IProvider,
  ISelectedValue
} from '../ScheduleAppointmentSlot/ScheduleAppointmentSlot';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import NavRoutes from 'src/constants/route/NavRoutes';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {
  getAppointmentTypeList,
  getAppointProviderList,
  getAvailableAppointmentSlots,
  getAvailableVaccine,
  getInsuranceDetails,
  getInsuranceFile,
  scheduleVaccineAppointment,
  updateVaccineAppointment,
  verifyAppointmentPortalToken
} from '@shared/services/AppointmentSchedulingService';
import {pdf} from '@react-pdf/renderer';
import ShingrixConsentFormDoc from '../ConsentForm/ShingrixConsentForm/ShingrixConsentFormDoc';
import moment from 'moment';
import HelperUtils, {getSlotTypeIndex} from '@shared/utils/HelperUtils';
import {State} from '@shared/store/reducers/Index';
import {
  DateFormatYYYYMMDD,
  appointmentsDateTimeFormat,
  appointmentsTime
} from '@shared/utils/DateTimeUtils';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {
  Appointment,
  InsuranceCardDetails,
  Vaccine,
  VaccineInfoKeyMapper,
  VerifyAppointmentSchedulerTokenResponse
} from '@shared/services/service-mappers/AppointmentSchedulingMapper';
import {IFluConsentFormState} from '../ConsentForm/FluConsentForm/FluConsentForm';
import FluConsentFormDoc from '../ConsentForm/FluConsentForm/FluConsentFormDoc';
import {
  CONSENT_METADATA_STATUS_MAPPER,
  FLU_VACCINE_QUESTION_ANSWER,
  VACCINE_INFO_KEY_MAPPER
} from '@shared/constants/Mapping';
import {
  _setAppointmentPortalCommonDetails,
  _setAppointmentPortalInsuranceDetails,
  _setAvailableVaccinesList,
  _setPatientAppointmentToken
} from '@shared/store/actions/AppointmentSchedularAction';
import {
  _getAppointemnetSlot,
  _setAppointmentPatientId
} from '@shared/store/actions/PaymentActions';
import Stepper from '@components/system/Stepper/Stepper';
import ConfirmationDialog from '@components/system/ConfirmationDialog/ConfirmationDialog';
import {getHolidaysInGuarantorPortal} from '@shared/services/HolidaysListService';
import SuccessIcon from '@components/system/MyIcons/SuccessIcon';
import AppointmentPortalPublicPageLayout from '../../components/AppointmentPortalPublicPageLayout/AppointmentPortalPublicPageLayout';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: DEFAULT_WHITE_COLOR
    },
    uploadInsuranceImageWrapper: {
      minHeight: 'auto',
      overflowX: 'hidden'
    },
    appointmentScheduledWrapper: {
      fontSize: '1.5rem',
      paddingBlock: '2rem',
      color: APP_PRIMARY_COLOR,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      flex: 1,
      width: '100%',
      minHeight: '100%',
      boxSizing: 'border-box'
    },
    successIcon: {
      marginBottom: '2rem',
      color: APP_SECONDARY_COLOR
    }
  })
);
export type VerificationStatus = 'not-verified' | 'verified' | 'verifying';
export interface VaccineConsentFormState {
  [VaccineInfoKeyMapper.SHINGRIX]: IShingrixConsentFormState;
  [VaccineInfoKeyMapper.FLUARIX_QUADRIVALENT]: IFluConsentFormState;
}

const INITIAL_CONSENT_FORM_STATE: VaccineConsentFormState = {
  [VACCINE_INFO_KEY_MAPPER.SHINGRIX]: {
    digitalSignatureURL: '',
    selectedShingrixVaccine: undefined,
    consent: false,
    consentFormDate: new Date()
  },
  [VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT]: {
    digitalSignatureURL: '',
    consentFormDate: new Date(),
    hasAllergyToIngredient: undefined,
    hasGuillianSyndrome: undefined,
    hasReactionToInfulenza: undefined,
    isSickToday: undefined,
    formCompletedBy: ''
  }
};
const AppointmentScheduledScreen = () => {
  const classes = useStyles();
  const {t} = useTranslation();
  return (
    <div className={classes.appointmentScheduledWrapper}>
      <SuccessIcon width={128} height={128} className={classes.successIcon} />
      <p>
        {t(
          'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.YOUR_APPOINTMENT_IS_SCHEDULED'
        )}
      </p>
    </div>
  );
};

// To show "Your appointment slot has been updated." text when the appointmnet is successfully updated.
const AppointmentUpdatedScreen = () => {
  const classes = useStyles();
  const {t} = useTranslation();
  return (
    <div className={classes.appointmentScheduledWrapper}>
      <SuccessIcon width={128} height={128} className={classes.successIcon} />
      <p>
        {t(
          'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.YOUR_APPOINTMENT_IS_UPDATED'
        )}
      </p>
    </div>
  );
};

const ScheduleAppointment = (): JSX.Element => {
  const classes = useStyles();
  const history = useHistory();
  const {t} = useTranslation();
  const {
    patientIdForAppointmentPortal: patientIdFromPaymentPortal,
    clinicIdForAppointmentPortal,
    isLogin: isLoggedInToPaymentPortal,
    apptDetails,
    appointmentSlot
  } = useSelector((state: State) => state.payments);

  const [
    appointmentSlotSelectedValues,
    setAppointmentSlotSelectedValues
  ] = useState<ISelectedValue>({
    appointmentDate: new Date(),
    slot: null,
    vaccine: null,
    availableSlots: [],
    busySlots: [],
    dayWindow: null,
    preferredTimeActiveTabIdx: 0,
    loadingAppointmentSlots: false,
    patient: null,
    holidays: [],
    appointmentType: null,
    provider: null
  });

  const [appointmentTypesList, setAppointmentTypesList] = useState<
    IAppointmentType[]
  >([]);

  const [availableProvidersList, setAvailableProvidersList] = useState<
    IProvider[]
  >([]);

  const {enqueueSnackbar, closeSnackbar} = useSnackbar();

  const location = useLocation<{fromPaymentPortal?: boolean}>();

  const [appointPortalCommonData, setAppointPortalCommonData] = useState({
    insuranceCardDetails: null as InsuranceCardDetails | null | undefined,
    appointmentPortalDetails: null as VerifyAppointmentSchedulerTokenResponse | null,
    isLoading: false,
    availableVaccinesList: []
  });

  const hasComeFromPaymentPortal = location.state?.fromPaymentPortal;

  useEffect(() => {
    if (location.state?.fromPaymentPortal) {
      return () => {
        _setAppointmentPatientId();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    appointmentPortalDetails,
    availableVaccinesList,
    insuranceCardDetails
  } = appointPortalCommonData;
  const {code} = useParams<{code: string}>();

  const logoutAction = async (): Promise<void> => {
    history.push(
      `/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.BASE}/${code}/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.LOGIN}`
    );
  };
  const dispatch = useDispatch();

  const init = async () => {
    setAppointPortalCommonData(p => ({...p, isLoading: true}));
    try {
      const verifyRes = await verifyAppointmentPortalToken();
      if (verifyRes.data) {
        // OVERRIDE THE PATIENT ID IF CAME TO SCHEDULE APPOINTMENT FROM PAYMENT PORTAL
        if (isLoggedInToPaymentPortal && patientIdFromPaymentPortal) {
          verifyRes.data.logged_in_patient.id = patientIdFromPaymentPortal;
          dispatch(
            _setAppointmentPortalCommonDetails({
              ...verifyRes.data,
              logged_in_patient: {
                id: patientIdFromPaymentPortal
              }
            })
          );
        } else {
          dispatch(_setAppointmentPortalCommonDetails(verifyRes.data));
        }

        setAppointPortalCommonData(prev => ({
          ...prev,
          appointmentPortalDetails: verifyRes.data
        }));
      }
      const insuranceRes = await getInsuranceDetails();
      let loggedInPatientInsurance:
        | InsuranceCardDetails
        | null
        | undefined = null;
      if (
        Array.isArray(insuranceRes.data.insurance) &&
        verifyRes.data.logged_in_patient.id
      ) {
        loggedInPatientInsurance = insuranceRes.data.insurance.find(
          ins => ins.patient_id === verifyRes.data.logged_in_patient.id
        );
      }
      if (loggedInPatientInsurance) {
        setAppointPortalCommonData(prev => ({
          ...prev,
          insuranceCardDetails: loggedInPatientInsurance
        }));
        dispatch(
          _setAppointmentPortalInsuranceDetails(loggedInPatientInsurance)
        );
      }
    } catch (err: any) {
      logoutAction();
    } finally {
      setAppointPortalCommonData(p => ({...p, isLoading: false}));
    }
  };

  const {patientAppointmentToken} = useSelector(
    (state: State) => state.appointmentPortal
  );

  useEffect(() => {
    init();
    if (!patientAppointmentToken) {
      dispatch(_setPatientAppointmentToken(code));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [uploadInsuranceStepState, setUploadInsuranceStepState] = useState({
    verificationStatus: !insuranceCardDetails?.id
      ? 'not-verified'
      : ('verified' as VerificationStatus),
    verificationProgressValue: 0,
    frontImg: null as File | null,
    backImg: null as File | null,
    loadingInsuranceInformation: false,
    existingFrontImg: null as File | null,
    existingBackImg: null as File | null
  });

  const updateProgressValueBy = (val: number, maxValue = 100) => {
    setUploadInsuranceStepState(p => ({
      ...p,
      verificationProgressValue:
        p.verificationProgressValue + val >= maxValue
          ? maxValue
          : p.verificationProgressValue + val
    }));
  };

  useEffect(() => {
    const getInsuranceImages = async (
      frontImgId: string,
      backImgId: string
    ) => {
      setUploadInsuranceStepState(p => ({
        ...p,
        loadingInsuranceInformation: true
      }));
      try {
        const resFrontImg = await getInsuranceFile(frontImgId);
        const resBackImg = await getInsuranceFile(backImgId);
        setUploadInsuranceStepState(p => ({
          ...p,
          frontImg: resFrontImg,
          backImg: resBackImg,
          existingFrontImg: resFrontImg,
          existingBackImg: resBackImg,
          verificationStatus: 'verified',
          verificationProgressValue: 0
        }));
      } catch (err: any) {
        enqueueSnackbar(
          err?.message
            ? err.message
            : t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.ERROR_GETTING_INSURANCE_CARDS'
              ),
          SnackBarConfig('e')
        );
      } finally {
        setUploadInsuranceStepState(p => ({
          ...p,
          loadingInsuranceInformation: false
        }));
      }
    };
    if (insuranceCardDetails?.file_id1 && insuranceCardDetails?.file_id2) {
      getInsuranceImages(
        insuranceCardDetails.file_id1,
        insuranceCardDetails.file_id2
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceCardDetails?.file_id1, insuranceCardDetails?.file_id2]);

  const [
    consentFormState,
    setConsentFormState
  ] = useState<VaccineConsentFormState>(INITIAL_CONSENT_FORM_STATE);

  const isConsentFormFilled = () => {
    const selectedVaccineName =
      appointmentSlotSelectedValues.vaccine?.vaccine_info_key;
    if (selectedVaccineName === VACCINE_INFO_KEY_MAPPER.SHINGRIX) {
      return (
        consentFormState[selectedVaccineName].consent &&
        consentFormState[selectedVaccineName].selectedShingrixVaccine &&
        !!consentFormState[selectedVaccineName].digitalSignatureURL
      );
    } else if (
      selectedVaccineName === VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT
    ) {
      return (
        consentFormState[selectedVaccineName].formCompletedBy &&
        consentFormState[selectedVaccineName].isSickToday &&
        consentFormState[selectedVaccineName].hasAllergyToIngredient &&
        consentFormState[selectedVaccineName].hasGuillianSyndrome &&
        consentFormState[selectedVaccineName].hasReactionToInfulenza
      );
    }
    return false;
  };
  const updateVerificationStatus = (verificationStatus: VerificationStatus) => {
    setUploadInsuranceStepState(p => ({
      ...p,
      verificationStatus,
      verificationProgressValue: 0
    }));
  };

  const handleFileChange = (fileType: 'front' | 'back', file: null | File) => {
    // change the verification status if a new file is uploaded or existing file is removed
    if (fileType === 'front') {
      setUploadInsuranceStepState(p => ({
        ...p,
        frontImg: file,
        verificationStatus: 'not-verified'
      }));
    } else {
      setUploadInsuranceStepState(p => ({
        ...p,
        backImg: file,
        verificationStatus: 'not-verified'
      }));
    }
  };

  const resetUploadInsuranceStepState = () => {
    setUploadInsuranceStepState(p => ({
      ...p,
      backImg: p.existingBackImg,
      frontImg: p.existingFrontImg,
      verificationStatus: insuranceCardDetails?.id
        ? 'verified'
        : 'not-verified',
      verificationProgressValue: 0,
      loadingInsuranceInformation: false
    }));
  };
  useEffect(() => {
    const fetchAppointmentSlot = async () => {
      let clinicId = appointmentPortalDetails?.clinic?.id as number;
      if (hasComeFromPaymentPortal && clinicIdForAppointmentPortal) {
        clinicId = clinicIdForAppointmentPortal;
      }
      const hasRequiredFilters =
        !!appointmentSlotSelectedValues.appointmentDate &&
        !!appointmentSlotSelectedValues.patient?.id &&
        !!appointmentSlotSelectedValues.appointmentType?.id &&
        !!clinicId &&
        (!!appointmentSlotSelectedValues.vaccine?.id ||
          !!appointmentSlotSelectedValues.provider?.id);

      if (!hasRequiredFilters) {
        setAppointmentSlotSelectedValues(p => ({
          ...p,
          availableSlots: [],
          busySlots: [],
          dayWindow: null,
          slot: null,
          loadingAppointmentSlots: false
        }));
        return;
      }
      try {
        const formattedDate = DateFormatYYYYMMDD(
          appointmentSlotSelectedValues.appointmentDate
        );
        closeSnackbar();
        setAppointmentSlotSelectedValues(p => ({
          ...p,
          loadingAppointmentSlots: true,
          availableSlots: [],
          busySlots: [],
          dayWindow: null,
          slot: null
        }));

        const params: {
          clinic_id: number;
          date: string;
          patient_id: number;
          appointment_type_id: number;
          vaccine_id?: number;
          provider_id?: number;
        } = {
          clinic_id: clinicId,
          date: formattedDate,
          patient_id: appointmentSlotSelectedValues.patient!.id,
          appointment_type_id:
            appointmentSlotSelectedValues.appointmentType!.id
        };

        if (appointmentSlotSelectedValues.vaccine?.id) {
          params.vaccine_id = appointmentSlotSelectedValues.vaccine.id;
        }
        if (appointmentSlotSelectedValues.provider?.id) {
          params.provider_id = appointmentSlotSelectedValues.provider.id;
        }

        const res = await getAvailableAppointmentSlots(params);

        if (Array.isArray(res.data?.rows)) {
          setAppointmentSlotSelectedValues(p => ({
            ...p,
            availableSlots: res.data.rows,
            busySlots: Array.isArray(res.data.busy_slots)
              ? res.data.busy_slots
              : [],
            dayWindow: res.data.day_window || null
          }));
        } else {
          setAppointmentSlotSelectedValues(p => ({
            ...p,
            availableSlots: [],
            busySlots: [],
            dayWindow: null,
            slot: null
          }));
        }
      } catch (err: any) {
        setAppointmentSlotSelectedValues(p => ({
          ...p,
          availableSlots: [],
          busySlots: [],
          dayWindow: null,
          slot: null
        }));
        enqueueSnackbar(
          err?.message
            ? err.message
            : t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.ERROR_GETTING_APPOINTMENT_SLOTS'
              ),
          SnackBarConfig('e')
        );
      } finally {
        setAppointmentSlotSelectedValues(p => ({
          ...p,
          loadingAppointmentSlots: false
        }));
      }
    };
    fetchAppointmentSlot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointmentSlotSelectedValues.appointmentDate,
    appointmentSlotSelectedValues.vaccine,
    appointmentSlotSelectedValues.patient,
    appointmentPortalDetails?.clinic.id,
    appointmentSlotSelectedValues.provider,
    appointmentSlotSelectedValues.appointmentType,
    clinicIdForAppointmentPortal
  ]);

  useEffect(() => {
    const fetchHolidays = async () => {
      let clinicId = appointmentPortalDetails?.clinic?.id as number;
      if (hasComeFromPaymentPortal && clinicIdForAppointmentPortal) {
        clinicId = clinicIdForAppointmentPortal;
      }
      if (!clinicId) {
        setAppointmentSlotSelectedValues(p => ({
          ...p,
          holidays: []
        }));
        return;
      }

      try {
        const res = await getHolidaysInGuarantorPortal({
          clinic_id: clinicId
        });
        if (Array.isArray(res.data.holidays)) {
          setAppointmentSlotSelectedValues(p => ({
            ...p,
            holidays: res.data.holidays.map((i: any) => ({
              date: moment(i.holiday_on).format('YYYY-MM-DD'),
              description: i.description || null
            }))
          }));
        } else {
          setAppointmentSlotSelectedValues(p => ({
            ...p,
            holidays: []
          }));
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      }
    };
    fetchHolidays();
  }, [appointmentPortalDetails?.clinic.id, clinicIdForAppointmentPortal]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchAppointmentTypes = async () => {
      let clinicId = appointmentPortalDetails?.clinic?.id as number;
      if (hasComeFromPaymentPortal && clinicIdForAppointmentPortal) {
        clinicId = clinicIdForAppointmentPortal;
      }
      if (!clinicId) return;
      try {
        const res = await getAppointmentTypeList(clinicId);
        if (res.data && Array.isArray(res.data)) {
          setAppointmentTypesList(res.data);

          if (
            res.data.length === 1 &&
            !appointmentSlotSelectedValues.appointmentType &&
            !apptDetails?.updateAppointment
          ) {
            setAppointmentSlotSelectedValues(prevState => ({
              ...prevState,
              appointmentType: res.data[0],
              slot: null,
              availableSlots: [],
              busySlots: [],
              dayWindow: null,
              vaccine: null,
              provider: null
            }));
          }
        } else {
          setAppointmentTypesList([]);
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      }
    };
    fetchAppointmentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointmentPortalDetails?.clinic.id,
    clinicIdForAppointmentPortal
    // enqueueSnackbar,
    // hasComeFromPaymentPortal
  ]);

  useEffect(() => {
    const fetchProviders = async () => {
      let clinicId = appointmentPortalDetails?.clinic?.id as number;
      if (hasComeFromPaymentPortal && clinicIdForAppointmentPortal) {
        clinicId = clinicIdForAppointmentPortal;
      }

      if (
        !clinicId ||
        !appointmentSlotSelectedValues.appointmentType?.id ||
        !appointmentSlotSelectedValues.appointmentType.is_provider_required
      ) {
        setAvailableProvidersList([]);
        return;
      }

      try {
        const res = await getAppointProviderList(
          clinicId,
          appointmentSlotSelectedValues.appointmentType?.id
        );
        if (res.data && Array.isArray(res.data)) {
          setAvailableProvidersList(res.data as IProvider[]);
        } else {
          setAvailableProvidersList([]);
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
        setAvailableProvidersList([]);
      }
    };

    if (appointmentSlotSelectedValues.provider) {
      setAppointmentSlotSelectedValues(p => ({...p, provider: null}));
      return;
    }

    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointmentSlotSelectedValues.appointmentType,
    appointmentPortalDetails,
    clinicIdForAppointmentPortal,
    enqueueSnackbar,
    hasComeFromPaymentPortal
  ]);

  useEffect(() => {
    const fetchVaccines = async () => {
      let clinicId = appointmentPortalDetails?.clinic?.id as number;
      if (hasComeFromPaymentPortal && clinicIdForAppointmentPortal) {
        clinicId = clinicIdForAppointmentPortal;
      }

      if (!clinicId) return;

      // Only fetch vaccines if appointment type is selected and is_provider_required is false
      if (
        !appointmentSlotSelectedValues.appointmentType ||
        appointmentSlotSelectedValues.appointmentType.is_provider_required !==
          false
      ) {
        setAppointPortalCommonData(p => ({
          ...p,
          availableVaccinesList: []
        }));
        dispatch(_setAvailableVaccinesList([]));
        return;
      }

      try {
        const vaccinesResponse = await getAvailableVaccine({
          params: {
            clinic_id: clinicId,
            appointment_type:
              appointmentSlotSelectedValues.appointmentType?.name_id
          }
        });

        if (vaccinesResponse.data?.length) {
          setAppointPortalCommonData(p => ({
            ...p,
            availableVaccinesList: vaccinesResponse.data
          }));
          dispatch(_setAvailableVaccinesList(vaccinesResponse.data));
        } else {
          setAppointPortalCommonData(p => ({
            ...p,
            availableVaccinesList: []
          }));
          dispatch(_setAvailableVaccinesList([]));
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
        setAppointPortalCommonData(p => ({
          ...p,
          availableVaccinesList: []
        }));
        dispatch(_setAvailableVaccinesList([]));
      }
    };
    fetchVaccines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointmentSlotSelectedValues.appointmentType,
    appointmentPortalDetails,
    clinicIdForAppointmentPortal
  ]);

  useEffect(() => {
    // To get the appointment slot details and prefill them in appointment details stepper section when the edit appointment link is clicked
    const getAppointmentDetails = async () => {
      if (
        apptDetails &&
        apptDetails.updateAppointment &&
        apptDetails?.patientId
        // apptDetails?.vaccineId
      ) {
        const clinicId = appointmentPortalDetails?.clinic?.id as number;

        if (clinicId) {
          let selectedVaccine: Vaccine | null = null;
          if (apptDetails?.vaccine) {
            selectedVaccine = apptDetails.vaccine;
          }
          let selectedPatient: any = null;
          if (Array.isArray(appointmentPortalDetails?.patient)) {
            selectedPatient = appointmentPortalDetails?.patient?.find(
              patient => patient.id === apptDetails?.patientId
            );
          }
          let selectedAppointment: Appointment | null = null;
          if (apptDetails?.appointment) {
            selectedAppointment = apptDetails.appointment;
          }
          let selectedProvider: IProvider | null = null;
          if (apptDetails?.provider) {
            selectedProvider = apptDetails.provider;
          }
          setAppointmentSlotSelectedValues(p => ({
            ...p,
            vaccine: selectedVaccine,
            appointmentType: selectedAppointment,
            provider: selectedProvider,
            appointmentDate: new Date(
              appointmentsDateTimeFormat(
                apptDetails?.appointmentAt,
                appointmentPortalDetails?.clinic?.zone ?? 'America/Chicago'
              )
            ),
            patient: selectedPatient,
            slot: {
              start_time: appointmentsTime(
                apptDetails?.appointmentAt,
                appointmentPortalDetails?.clinic?.zone ?? 'America/Chicago'
              ),
              end_time: appointmentsTime(
                moment(apptDetails?.appointmentAt).add(
                  apptDetails?.duration || 15,
                  'minutes'
                ),
                appointmentPortalDetails?.clinic?.zone ?? 'America/Chicago'
              ),
              duration: apptDetails?.duration || 15
            },
            preferredTimeActiveTabIdx:
              getSlotTypeIndex(
                appointmentsTime(
                  apptDetails?.appointmentAt,
                  appointmentPortalDetails?.clinic?.zone ?? 'America/Chicago'
                )
              ) === 0
                ? 0
                : 1
          }));

          dispatch(
            _getAppointemnetSlot({
              appointmentSlot: appointmentsTime(
                apptDetails?.appointmentAt,
                appointmentPortalDetails?.clinic?.zone ?? 'America/Chicago'
              )
            })
          );
        }
      }
    };

    getAppointmentDetails();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apptDetails, appointPortalCommonData, appointmentPortalDetails]);

  useEffect(() => {
    if (
      !appointmentSlotSelectedValues.vaccine &&
      !apptDetails.updateAppointment &&
      availableVaccinesList.length === 1
    ) {
      setAppointmentSlotSelectedValues(p => ({
        ...p,
        vaccine: availableVaccinesList[0]
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableVaccinesList, apptDetails]);

  // If there is only one patient then set the patient as the default
  useEffect(() => {
    if (
      appointmentPortalDetails?.patient?.length === 1 &&
      !apptDetails.updateAppointment &&
      !appointmentSlotSelectedValues.patient
    ) {
      setAppointmentSlotSelectedValues(p => ({
        ...p,
        patient: appointmentPortalDetails?.patient?.[0]
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the vaccine is changed reset the consent form state
  useEffect(() => {
    setConsentFormState(INITIAL_CONSENT_FORM_STATE);
  }, [appointmentSlotSelectedValues.vaccine]);

  const [schedulingAppointmentState, setSchedulingAppointmentState] = useState({
    isSchedulingAppointment: false
  });

  const scheduleAnAppointment = async () => {
    if (!appointmentPortalDetails?.clinic.id) {
      return;
    }
    try {
      setSchedulingAppointmentState(p => ({
        ...p,
        isSchedulingAppointment: true
      }));
      const payload = new FormData();
      const selectedPatient = appointmentSlotSelectedValues?.patient;
      const patientName = HelperUtils.formCapitalizeFullName(
        selectedPatient?.first_name,
        selectedPatient?.last_name,
        selectedPatient?.middle_name
      );
      const patientDOB = selectedPatient?.dob as string;
      const formattedPatientDOB = HelperUtils.formatDisplayDate(patientDOB);
      const appointmentDate = moment(
        appointmentSlotSelectedValues.appointmentDate
      ).startOf('day');
      const time = moment(
        appointmentSlotSelectedValues.slot?.start_time,
        'hh:mm A'
      );
      const appointmentDateTime = appointmentDate
        .hour(time.hours())
        .minute(time.minutes())
        .format('MM/DD/yyyy HH:mm');
      const appointmentDateWithClinicTimeZone = HelperUtils.addTimeZoneToDate(
        appointmentDateTime,
        'MM/DD/yyyy HH:mm',
        appointmentPortalDetails?.clinic?.zone ?? 'America/Chicago'
      );
      const selectedVaccineInfoKey =
        appointmentSlotSelectedValues.vaccine?.vaccine_info_key;
      if (selectedVaccineInfoKey) {
        let blob = null;
        if (selectedVaccineInfoKey === VACCINE_INFO_KEY_MAPPER.SHINGRIX) {
          const consentFormFormattedDate = HelperUtils.formatDisplayDate(
            consentFormState[selectedVaccineInfoKey].consentFormDate
          );
          const consentFormFormattedTime = moment(
            consentFormState[selectedVaccineInfoKey].consentFormDate
          ).format('h:mm A');
          blob = await pdf(
            <ShingrixConsentFormDoc
              date={consentFormFormattedDate}
              signatureURL={
                consentFormState[selectedVaccineInfoKey].digitalSignatureURL
              }
              time={consentFormFormattedTime}
              vaccine={
                consentFormState[selectedVaccineInfoKey]
                  .selectedShingrixVaccine ?? 'no'
              }
            />
          ).toBlob();
        } else if (
          selectedVaccineInfoKey ===
          VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT
        ) {
          const consentFormFormattedDate = HelperUtils.formatDisplayDate(
            consentFormState[selectedVaccineInfoKey].consentFormDate
          );
          const consentFormFormattedTime = moment(
            consentFormState[selectedVaccineInfoKey].consentFormDate
          ).format('h:mm A');
          const questionAndAnswers = [
            {
              question: t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_1'
              ),
              answer: consentFormState[selectedVaccineInfoKey].isSickToday
            },
            {
              question: t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_2'
              ),
              answer:
                consentFormState[selectedVaccineInfoKey].hasAllergyToIngredient
            },
            {
              question: t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_3'
              ),
              answer:
                consentFormState[selectedVaccineInfoKey].hasReactionToInfulenza
            },
            {
              question: t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_4'
              ),
              answer:
                consentFormState[selectedVaccineInfoKey].hasGuillianSyndrome
            }
          ];

          const doesStatusNeedsAttention = questionAndAnswers.some(
            item =>
              item.answer === t(FLU_VACCINE_QUESTION_ANSWER.YES) ||
              item.answer === t(FLU_VACCINE_QUESTION_ANSWER.DONT_KNOW)
          );

          const consentMeta = {
            vaccine_id: appointmentSlotSelectedValues.vaccine?.id,
            status: doesStatusNeedsAttention
              ? CONSENT_METADATA_STATUS_MAPPER.NEEDS_ATTENTION
              : CONSENT_METADATA_STATUS_MAPPER.COMPLETED,
            questionnaire: questionAndAnswers,
            meta: {}
          };
          payload.append('consent_meta', JSON.stringify(consentMeta));
          blob = await pdf(
            <FluConsentFormDoc
              date={consentFormFormattedDate}
              time={consentFormFormattedTime}
              {...consentFormState[selectedVaccineInfoKey]}
              patientName={patientName}
              patientDOB={formattedPatientDOB}
            />
          ).toBlob();
        }
        // Must have blob to send it in the payload
        if (!blob) {
          throw new Error('Consent form file is required');
        }
        if (appointmentSlotSelectedValues?.vaccine?.id) {
          payload.append(
            'vaccine_id',
            `${appointmentSlotSelectedValues?.vaccine?.id}`
          );
        }
        payload.append('file', blob);
      }
      payload.append('appointment_date', appointmentDateWithClinicTimeZone);
      payload.append(
        'duration',
        `${appointmentSlotSelectedValues.slot?.duration || 15}`
      );
      payload.append(
        'patient_id',
        `${appointmentSlotSelectedValues?.patient?.id}`
      );
      if (hasComeFromPaymentPortal && clinicIdForAppointmentPortal) {
        payload.append('clinic_id', `${clinicIdForAppointmentPortal ?? ''}`);
      } else {
        payload.append(
          'clinic_id',
          `${appointmentPortalDetails?.clinic.id ?? ''}`
        );
      }
      if (appointmentSlotSelectedValues?.provider?.id) {
        payload.append(
          'provider_id',
          `${appointmentSlotSelectedValues?.provider?.id}`
        );
      }
      payload.append(
        'appointment_type_id',
        `${appointmentSlotSelectedValues?.appointmentType?.id}`
      );

      if (!apptDetails?.updateAppointment) {
        await scheduleVaccineAppointment(payload);
      }

      // Call the updateVaccineAppointment API only when clicked on the edit link
      if (apptDetails?.updateAppointment) {
        if (
          appointmentSlot ===
            appointmentsTime(
              apptDetails?.appointmentAt,
              appointmentPortalDetails?.clinic?.zone || 'America/Chicago'
            ) &&
          HelperUtils.formatDisplayDate(
            new Date(
              appointmentsDateTimeFormat(
                apptDetails?.appointmentAt,
                appointmentPortalDetails?.clinic?.zone || 'America/Chicago'
              )
            )
          ) ===
            HelperUtils.formatDisplayDate(
              appointmentSlotSelectedValues.appointmentDate
            )
        ) {
          // If the appointment slot is unchanged pass appointment_slot_unchanged as true in payload
          payload.append('appointment_slot_unchanged', true.toString());
        }
        await updateVaccineAppointment(apptDetails?.appointmentId, payload);
      }
    } finally {
      setSchedulingAppointmentState(p => ({
        ...p,
        isSchedulingAppointment: false
      }));
    }
  };

  const [confirmationDialog, setConfirmationDialog] = useState(false);

  const navigateToPortalHomeScreen = () => {
    if (location.state?.fromPaymentPortal) {
      history.goBack();
    } else {
      history.push(
        `/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.BASE}/${code}/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.HOME}`
      );
    }
  };

  const stepperRef = useRef<{
    goToNextStep: () => void;
  }>(null);
  return (
    <AppointmentPortalPublicPageLayout
      className="payment-login-page appointment-scheduler-schedule-page"
      testId="pcl-appointmentPortalSchedulePage">
      <div className={classes.root}>
      <Stepper
        ref={stepperRef}
        steps={[
          {
            title: t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.EDIT_INSURANCE_DETAILS_STEP_LABEL'
            ),
            content: (
              <UploadOrEditInsurance
                editInsurance={!!insuranceCardDetails}
                className={classes.uploadInsuranceImageWrapper}
                {...uploadInsuranceStepState}
                updateFileChange={handleFileChange}
                updateVerificationStatus={updateVerificationStatus}
                resetChanges={resetUploadInsuranceStepState}
                updateProgressValueBy={updateProgressValueBy}
              />
            ),
            backButtonLabel: t('COMMON.CANCEL'),
            onBackButtonClick: () => {
              navigateToPortalHomeScreen();
              return false;
            },
            nextButtonDisabledState:
              uploadInsuranceStepState.verificationStatus !== 'verified',
            nextButtonLabel: apptDetails?.updateAppointment
              ? t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.UPDATE_APPOINTMENT_SLOT'
                )
              : t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.SELECT_APPOINTMENT_SLOT'
                )
          },
          {
            title: t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.SELECT_APPOINTMENT_SLOT_STEP_LABEL'
            ),
            content: (
              <ScheduleAppointmentSlot
                selectedValues={appointmentSlotSelectedValues}
                updateSelectedValues={setAppointmentSlotSelectedValues}
                patientsList={
                  Array.isArray(appointmentPortalDetails?.patient)
                    ? appointmentPortalDetails?.patient
                    : []
                }
                availableVaccinesList={availableVaccinesList}
                isEditing={apptDetails?.updateAppointment}
                appointmentSlotDetails={{
                  date: appointmentsDateTimeFormat(
                    apptDetails?.appointmentAt,
                    appointmentPortalDetails?.clinic?.zone ?? 'America/Chicago'
                  ),
                  duration: apptDetails?.duration || 15,
                  endTime: appointmentsTime(
                    moment(apptDetails?.appointmentAt).add(
                      apptDetails?.duration || 15,
                      'minutes'
                    ),
                    appointmentPortalDetails?.clinic?.zone ?? 'America/Chicago'
                  ),
                  startTime: appointmentsTime(
                    apptDetails?.appointmentAt,
                    appointmentPortalDetails?.clinic?.zone ?? 'America/Chicago'
                  ),
                  vaccineName: apptDetails?.vaccineName,
                  appointmentTypename: apptDetails?.appointment?.label,
                  providerName: `${apptDetails?.provider?.first_name} ${apptDetails?.provider?.last_name}`
                }}
                appointmentTypesList={appointmentTypesList}
                availableProvidersList={availableProvidersList}
              />
            ),
            onNextButtonClick: async () => {
              if (apptDetails?.updateAppointment) {
                const isSameDateAsBooked =
                  apptDetails?.updateAppointment &&
                  moment(appointmentSlotSelectedValues?.appointmentDate).format(
                    'YYYY-MM-DD'
                  ) ===
                    moment(
                      new Date(
                        appointmentsDateTimeFormat(
                          apptDetails?.appointmentAt,
                          appointmentPortalDetails?.clinic?.zone ??
                            'America/Chicago'
                        )
                      )
                    ).format('YYYY-MM-DD');
                const isSameSlotAsBooked =
                  appointmentsTime(
                    apptDetails?.appointmentAt,
                    appointmentPortalDetails?.clinic?.zone ?? 'America/Chicago'
                  ) === appointmentSlotSelectedValues?.slot?.start_time;
                const isSameVaccineAsBooked =
                  appointmentSlotSelectedValues?.vaccine?.id ===
                  apptDetails?.vaccineId;
                if (
                  isSameDateAsBooked &&
                  isSameSlotAsBooked &&
                  isSameVaccineAsBooked
                ) {
                  setConfirmationDialog(true);
                  return false;
                }
              }
              if (appointmentSlotSelectedValues.vaccine) {
                return true;
              }
              try {
                await scheduleAnAppointment();
                return true;
              } catch (err: any) {
                enqueueSnackbar(err?.message ?? '', SnackBarConfig('e'));
                return false;
              }
            },
            nextButtonLoadingState:
              !appointmentSlotSelectedValues.vaccine &&
              schedulingAppointmentState.isSchedulingAppointment,
            nextButtonDisabledState:
              !appointmentSlotSelectedValues.appointmentDate ||
              !appointmentSlotSelectedValues.slot ||
              !appointmentSlotSelectedValues.patient?.id ||
              !appointmentSlotSelectedValues.appointmentType?.id ||
              (!appointmentSlotSelectedValues.vaccine?.id &&
                !appointmentSlotSelectedValues.provider?.id),
            nextButtonLabel: appointmentSlotSelectedValues.vaccine
              ? t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.PROVIDE_CONSENT'
                )
              : apptDetails?.updateAppointment
              ? t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.UPDATE_APPOINTMENT'
                )
              : t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.CONFIRM_APPOINTMENT'
                )
          },
          ...(appointmentSlotSelectedValues.vaccine
            ? [
                {
                  title: t(
                    'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.PROVIDE_CONSENT_STEP_LABEL'
                  ),
                  content: (
                    <ConsentForm
                      {...consentFormState}
                      selectedVaccine={appointmentSlotSelectedValues.vaccine}
                      updateConsentFormState={setConsentFormState}
                    />
                  ),
                  onNextButtonClick: async () => {
                    try {
                      await scheduleAnAppointment();
                      return true;
                    } catch (err: any) {
                      enqueueSnackbar(err?.message ?? '', SnackBarConfig('e'));
                      return false;
                    }
                  },
                  nextButtonLoadingState:
                    schedulingAppointmentState.isSchedulingAppointment,
                  nextButtonDisabledState: !isConsentFormFilled(),
                  nextButtonLabel: apptDetails?.updateAppointment
                    ? t(
                        'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.UPDATE_APPOINTMENT'
                      )
                    : t(
                        'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.CONFIRM_APPOINTMENT'
                      )
                }
              ]
            : []),
          {
            title: apptDetails?.updateAppointment
              ? t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.APPOINTMENT_UPDATED'
                )
              : t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT.APPOINTMENT_SCHEDULED'
                ),
            content: apptDetails?.updateAppointment ? (
              <AppointmentUpdatedScreen />
            ) : (
              <AppointmentScheduledScreen />
            ),
            hideBackButton: true,
            onNextButtonClick: () => {
              navigateToPortalHomeScreen();
              return false;
            },
            nextButtonLabel: t('APPOINTMENTS_SCHEDULAR_PORTAL.GO_HOME')
          }
        ]}
      />
      {confirmationDialog && (
        <ConfirmationDialog
          open={confirmationDialog}
          title={t('COMMON.APPOINTMENT_SLOT_NOT_CHANGED')}
          description={t('COMMON.APPOINTMENT_SLOT_CONFIRMATION')}
          confirmButtonText={t('PAYMENT_PLANS.CONFIRM')}
          cancelButtonText={t('PAYMENT_PLANS.CANCEL')}
          onClose={response => {
            if (response === 'confirm') {
              setConfirmationDialog(false);
              if (stepperRef.current) {
                stepperRef.current.goToNextStep();
              }
            } else {
              setConfirmationDialog(false);
            }
          }}
        />
      )}
      </div>
    </AppointmentPortalPublicPageLayout>
  );
};

export default ScheduleAppointment;
